import React, { useMemo, useState } from "react";
import Dropdown from "../../../components/Dropdown/Dropdown";
import AdditionalInfoField from "../../../components/AdditionalInfoField/AdditionalInfoField";
import {
  aiBehaviors,
  getRolesForMeetingType,
  getSpeakingWithOptions,
  industries,
  languageOptions,
  meetingTypes,
} from "../../../config/meetingOptions";

const DURATION_OPTIONS = [
  { value: "5", label: "5 min" },
  { value: "10", label: "10 min" },
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
];

const hasOption = (value, options = []) =>
  options.some((opt) => opt.value === value);

const withCustomOption = (options = []) => {
  if (hasOption("custom", options)) return options;
  return [{ value: "custom", label: "Create Your Own" }, ...options];
};

const parseGoals = (raw = "") => {
  const seen = new Set();
  const parsed = [];
  raw
    .split(/\n|,/)
    .map((v) => v.trim())
    .filter(Boolean)
    .forEach((goal) => {
      const key = goal.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        parsed.push(goal);
      }
    });
  return parsed;
};

const toFormState = (meetingData) => {
  const meetingTypeRaw = String(meetingData?.meetingType || "");
  const meetingType = meetingTypeRaw
    ? hasOption(meetingTypeRaw, meetingTypes)
      ? meetingTypeRaw
      : "custom"
    : "";

  const industryRaw = String(meetingData?.industry || "");
  const industry = industryRaw
    ? hasOption(industryRaw, industries)
      ? industryRaw
      : "custom"
    : "";

  const roleOptions = withCustomOption(getRolesForMeetingType(meetingType));
  const roleRaw = String(meetingData?.userRole || "");
  const userRole = roleRaw
    ? hasOption(roleRaw, roleOptions)
      ? roleRaw
      : "custom"
    : "";

  const speakingWithOptions = withCustomOption(
    getSpeakingWithOptions(meetingType, userRole)
  );
  const speakingWithRaw = String(meetingData?.speakingWith || "");
  const speakingWith = speakingWithRaw
    ? hasOption(speakingWithRaw, speakingWithOptions)
      ? speakingWithRaw
      : "custom"
    : "";

  const allGoals = [
    ...(Array.isArray(meetingData?.goals)
      ? meetingData.goals
      : [meetingData?.goals]),
    ...(Array.isArray(meetingData?.customGoals)
      ? meetingData.customGoals
      : [meetingData?.customGoals]),
  ]
    .map((g) => String(g || "").trim())
    .filter(Boolean);

  return {
    meetingType,
    customMeetingType: meetingType === "custom" ? meetingTypeRaw : "",
    industry,
    customIndustry: industry === "custom" ? industryRaw : "",
    userRole,
    customUserRole: userRole === "custom" ? roleRaw : "",
    speakingWith,
    customSpeakingWith: speakingWith === "custom" ? speakingWithRaw : "",
    languageLabel: meetingData?.languageLabel || "English",
    aiBehavior: meetingData?.aiBehavior || "professional",
    duration: String(meetingData?.duration || 10),
    goalsInput: allGoals.join("\n"),
    additionalInfo: meetingData?.additionalInfo || "",
  };
};

export default function MeetingDetailsEditor({ meetingData, onSave, onCancel, isSaving = false }) {
  const [formData, setFormData] = useState(() => toFormState(meetingData));
  const [invalid, setInvalid] = useState({});

  const roleOptions = useMemo(
    () => withCustomOption(getRolesForMeetingType(formData.meetingType)),
    [formData.meetingType]
  );
  const speakingWithOptions = useMemo(
    () =>
      withCustomOption(
        getSpeakingWithOptions(formData.meetingType, formData.userRole)
      ),
    [formData.meetingType, formData.userRole]
  );

  const handleFieldChange = (name, value) => {
    setInvalid((prev) => {
      if (name === "customMeetingType") return { ...prev, meetingType: false };
      if (name === "customIndustry") return { ...prev, industry: false };
      if (name === "customUserRole") return { ...prev, userRole: false };
      if (name === "customSpeakingWith") return { ...prev, speakingWith: false };
      return { ...prev, [name]: false };
    });
    setFormData((prev) => {
      if (name === "meetingType") {
        return {
          ...prev,
          meetingType: value,
          customMeetingType: "",
          userRole: "",
          customUserRole: "",
          speakingWith: "",
          customSpeakingWith: "",
        };
      }
      if (name === "userRole") {
        return {
          ...prev,
          userRole: value,
          customUserRole: "",
          speakingWith: "",
          customSpeakingWith: "",
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    const effectiveMeetingType =
      formData.meetingType === "custom"
        ? formData.customMeetingType.trim()
        : formData.meetingType;
    const effectiveIndustry =
      formData.industry === "custom"
        ? formData.customIndustry.trim()
        : formData.industry;
    const effectiveUserRole =
      formData.userRole === "custom"
        ? formData.customUserRole.trim()
        : formData.userRole;
    const effectiveSpeakingWith =
      formData.speakingWith === "custom"
        ? formData.customSpeakingWith.trim()
        : formData.speakingWith;
    const durationNumber = Number(formData.duration || 0);

    const nextInvalid = {
      meetingType: !effectiveMeetingType,
      industry: !effectiveIndustry,
      userRole: !effectiveUserRole,
      speakingWith: !effectiveSpeakingWith,
      languageLabel: !formData.languageLabel,
      aiBehavior: !formData.aiBehavior,
      duration: !Number.isFinite(durationNumber) || durationNumber <= 0,
      additionalInfo: (formData.additionalInfo?.length || 0) > 4000,
    };

    if (Object.values(nextInvalid).some(Boolean)) {
      setInvalid(nextInvalid);
      return;
    }

    const goals = parseGoals(formData.goalsInput);
    await onSave?.({
      ...meetingData,
      meetingType: effectiveMeetingType,
      customMeeting: null,
      industry: effectiveIndustry,
      customIndustry: null,
      userRole: effectiveUserRole,
      customRole: null,
      speakingWith: effectiveSpeakingWith,
      customSpeakingWith: null,
      goals,
      customGoals: [],
      languageLabel: formData.languageLabel || "English",
      aiBehavior: formData.aiBehavior,
      duration: durationNumber,
      additionalInfo: formData.additionalInfo || "",
    });
  };

  return (
    <div className="mr-edit-form" role="form" aria-label="Edit meeting details">
      <div className="mr-edit-field">
        <label>Conversation Type</label>
        <Dropdown
          options={meetingTypes}
          value={formData.meetingType}
          onChange={(v) => handleFieldChange("meetingType", v)}
          placeholder="Select conversation type..."
          invalid={!!invalid.meetingType}
          disabled={isSaving}
        />
      </div>

      {formData.meetingType === "custom" && (
        <div className="mr-edit-field">
          <label>Custom Conversation Type</label>
          <input
            value={formData.customMeetingType}
            onChange={(e) => handleFieldChange("customMeetingType", e.target.value)}
            className={`mr-edit-input ${invalid.meetingType ? "is-invalid" : ""}`}
            placeholder="Describe the conversation type"
            disabled={isSaving}
          />
        </div>
      )}

      <div className="mr-edit-field">
        <label>Industry</label>
        <Dropdown
          options={industries}
          value={formData.industry}
          onChange={(v) => handleFieldChange("industry", v)}
          placeholder="Select industry..."
          invalid={!!invalid.industry}
          disabled={isSaving}
        />
      </div>

      {formData.industry === "custom" && (
        <div className="mr-edit-field">
          <label>Custom Industry</label>
          <input
            value={formData.customIndustry}
            onChange={(e) => handleFieldChange("customIndustry", e.target.value)}
            className={`mr-edit-input ${invalid.industry ? "is-invalid" : ""}`}
            placeholder="Describe the industry"
            disabled={isSaving}
          />
        </div>
      )}

      <div className="mr-edit-field">
        <label>My Role</label>
        <Dropdown
          options={roleOptions}
          value={formData.userRole}
          onChange={(v) => handleFieldChange("userRole", v)}
          placeholder="Select your role..."
          invalid={!!invalid.userRole}
          disabled={isSaving}
        />
      </div>

      {formData.userRole === "custom" && (
        <div className="mr-edit-field">
          <label>Custom Role</label>
          <input
            value={formData.customUserRole}
            onChange={(e) => handleFieldChange("customUserRole", e.target.value)}
            className={`mr-edit-input ${invalid.userRole ? "is-invalid" : ""}`}
            placeholder="Describe your role"
            disabled={isSaving}
          />
        </div>
      )}

      <div className="mr-edit-field">
        <label>Speaking With</label>
        <Dropdown
          options={speakingWithOptions}
          value={formData.speakingWith}
          onChange={(v) => handleFieldChange("speakingWith", v)}
          placeholder="Select who you're speaking with..."
          invalid={!!invalid.speakingWith}
          disabled={isSaving}
        />
      </div>

      {formData.speakingWith === "custom" && (
        <div className="mr-edit-field">
          <label>Custom Speaking With</label>
          <input
            value={formData.customSpeakingWith}
            onChange={(e) =>
              handleFieldChange("customSpeakingWith", e.target.value)
            }
            className={`mr-edit-input ${invalid.speakingWith ? "is-invalid" : ""}`}
            placeholder="Describe who you're speaking with"
            disabled={isSaving}
          />
        </div>
      )}

      <div className="mr-edit-field">
        <label>Language</label>
        <Dropdown
          options={languageOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
          value={formData.languageLabel}
          onChange={(v) => handleFieldChange("languageLabel", v)}
          placeholder="Select language..."
          invalid={!!invalid.languageLabel}
          disabled={isSaving}
        />
      </div>

      <div className="mr-edit-field">
        <label>AI Behavior</label>
        <Dropdown
          options={aiBehaviors}
          value={formData.aiBehavior}
          onChange={(v) => handleFieldChange("aiBehavior", v)}
          placeholder="Select behavior..."
          invalid={!!invalid.aiBehavior}
          disabled={isSaving}
        />
      </div>

      <div className="mr-edit-field">
        <label>Duration</label>
        <Dropdown
          options={DURATION_OPTIONS}
          value={formData.duration}
          onChange={(v) => handleFieldChange("duration", v)}
          placeholder="Select duration..."
          invalid={!!invalid.duration}
          disabled={isSaving}
        />
      </div>

      <div className="mr-edit-field">
        <label>Goals (one per line)</label>
        <textarea
          value={formData.goalsInput}
          onChange={(e) => handleFieldChange("goalsInput", e.target.value)}
          className="mr-edit-textarea"
          placeholder="List goals for this conversation"
          disabled={isSaving}
        />
      </div>

      <AdditionalInfoField
        value={formData.additionalInfo ?? ""}
        invalid={!!invalid.additionalInfo}
        onChange={(val) => handleFieldChange("additionalInfo", val)}
        disabled={isSaving}
        wrapperClassName="mr-edit-field"
        counterClassName="mr-edit-counter"
        textareaClassName="mr-edit-textarea"
      />

      <div className="mr-edit-actions">
        <button type="button" className="mr-edit-btn mr-edit-btn--ghost" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
        <button type="button" className="mr-edit-btn mr-edit-btn--primary" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Update"}
        </button>
      </div>
    </div>
  );
}
